// front/src/utils/index.js

import { toast } from "react-toastify";
import { parseApiError } from "./parseApiError";
import { tt as bridgeT } from "../i18n/i18nBridge";

/**
 * Read "lang" from URL query string (e.g. ?lang=fa).
 *
 * @returns {string|null} Language code from URL or null if not found.
 */
export function getLanguageFromURL() {
  const regex = new RegExp("[\\?&]lang=([^&#]*)");
  const results = regex.exec(window.location.search);
  return results === null ? null : decodeURIComponent(results[1].replace(/\+/g, " "));
}

/**
 * Normalize language codes to a consistent format.
 * Example: "FA" -> "fa"
 *
 * @param {string} lang
 * @returns {string}
 */
export function normalizeLang(lang) {
  return String(lang || "").trim().toLowerCase();
}

/**
 * Get the current application language.
 *
 * Priority:
 * 1) localStorage ("lang")
 * 2) URL query param (?lang=)
 * 3) fallback ("fa")
 *
 * @returns {string} Current language code.
 */
export function getCurrentLanguage() {
  const stored = normalizeLang(localStorage.getItem("lang"));
  if (stored) return stored;

  const fromUrl = normalizeLang(getLanguageFromURL());
  if (fromUrl) return fromUrl;

  return "fa";
}

/**
 * Persist the application language in localStorage.
 *
 * @param {string} lang - Language code ("fa", "en", etc.)
 */
export function setCurrentLanguage(lang) {
  const normalized = normalizeLang(lang);
  if (!normalized) return;
  localStorage.setItem("lang", normalized);
}

/**
 * TradingView chart default props.
 * Note: "locale" follows the same language policy as the app.
 */
export const ChartDefaultProps = {
  symbol: "AAPL",
  interval: "D",
  datafeedUrl: "https://demo_feed.tradingview.com",
  library_path: "/charting_library/",
  charts_storage_url: "https://saveload.tradingview.com",
  charts_storage_api_version: "1.1",
  client_id: "tradingview.com",
  user_id: "public_user_id",
  fullscreen: true,
  autosize: true,
  studies_overrides: {},
  container_id: "tv_chart_container",
  disabled_features: ["use_localstorage_for_settings"],
  enabled_features: ["study_templates"],
  locale: getCurrentLanguage(),
};

/**
 * Show a toast message (success or error).
 *
 * @param {("success"|"error")} type - Toast type.
 * @param {string} text - Message to display.
 */
export const showToast = (type, text) => {
  toast(String(text || ""), {
    theme: "colored",
    type: type === "success" ? toast.TYPE.SUCCESS : toast.TYPE.ERROR,
    position: toast.POSITION.BOTTOM_RIGHT,
    autoClose: 5000,
    hideProgressBar: true,
    closeButton: true,
  });
};

// -----------------------------
// ✅ i18n-aware API error mapping
// -----------------------------

const mapApiErrorCodeToI18nKey = (rawCode) => {
  const c = String(rawCode || "").trim();
  if (!c) return null;
  return `api_errors.${c}`;
};

const buildApiErrorKeyCandidates = (rawCode) => {
  const c = String(rawCode || "").trim();
  if (!c) return [];

  const out = [mapApiErrorCodeToI18nKey(c)];
  const parts = c.split(".").filter(Boolean);

  if (parts.length > 1) {
    out.push(`api_errors.${parts.slice(1).join(".")}`);
    out.push(`api_errors.${parts[parts.length - 1]}`);
  }

  return [...new Set(out.filter(Boolean))];
};

const TOAST_ERROR_OPTIONS = {
  theme: "colored",
  type: toast.TYPE.ERROR,
  position: toast.POSITION.BOTTOM_RIGHT,
  autoClose: 5000,
  hideProgressBar: true,
  closeButton: true,
};

function extractBackendPayload(err) {
  let cur = err;

  for (let i = 0; i < 7; i++) {
    if (!cur || typeof cur !== "object") break;

    const res = cur?.response;
    const data = res?.data;

    if (data && typeof data === "object") {
      const msg =
        (typeof data.message === "string" && data.message.trim()) ||
        (typeof data.detail === "string" && data.detail.trim()) ||
        null;

      const code =
        (typeof data.code === "string" && data.code.trim()) ||
        (typeof data?.extra?.code === "string" && data.extra.code.trim()) ||
        null;

      if (msg || code) {
        return { status: res?.status, data, message: msg, code };
      }
    }

    const maybeMsg =
      (typeof cur.message === "string" && cur.message.trim()) ||
      (typeof cur.detail === "string" && cur.detail.trim()) ||
      null;

    const maybeCode =
      (typeof cur.code === "string" && cur.code.trim()) ||
      (typeof cur?.extra?.code === "string" && cur.extra.code.trim()) ||
      null;

    if (maybeMsg || maybeCode) {
      return { status: undefined, data: cur, message: maybeMsg, code: maybeCode };
    }

    cur =
      cur.error ||
      cur.err ||
      cur.cause ||
      cur.originalError ||
      cur.original ||
      cur.reason ||
      cur.value;

    if (!cur) break;
  }

  return null;
}

const isUselessBackendMessage = (msg) => {
  const s = String(msg || "").trim().toLowerCase();
  return s === "validation error" || s === "validation_error";
};

const isAxiosGenericMessage = (msg) => {
  const s = String(msg || "").trim().toLowerCase();
  return (
    s.startsWith("request failed with status code") ||
    s === "network error" ||
    s === "timeout" ||
    s === "canceled" ||
    s === "cancelled"
  );
};

const pickTranslationIfExists = (t, key) => {
  if (typeof t !== "function" || !key) return null;

  const c1 = t(key, "__MISSING__");
  if (typeof c1 === "string" && c1.trim() && c1 !== "__MISSING__" && c1 !== key) return c1;

  const c2 = t(key, key);
  if (typeof c2 === "string" && c2.trim() && c2 !== key) return c2;

  return null;
};

const firstString = (v) => {
  if (!v) return null;
  if (typeof v === "string") return v.trim() ? v.trim() : null;

  if (Array.isArray(v)) {
    for (const item of v) {
      const s = firstString(item);
      if (s) return s;
    }
    return null;
  }

  if (typeof v === "object") {
    for (const k of Object.keys(v)) {
      const s = firstString(v[k]);
      if (s) return s;
    }
    return null;
  }

  return null;
};

const normalizeFieldsObj = (obj) => {
  if (!obj || typeof obj !== "object") return null;
  if (obj.detail && typeof obj.detail === "object") return obj.detail;
  return obj;
};

const pickFirstFieldError = (fieldsObj) => {
  const obj = normalizeFieldsObj(fieldsObj);
  if (!obj) return null;

  const preferred = [
    "phone_number",
    "username",
    "email",
    "accepted_terms",
    "accepted_privacy",
    "non_field_errors",
  ];

  for (const k of preferred) {
    if (obj[k] != null) {
      const s = firstString(obj[k]);
      if (s) return s;
    }
  }

  const keys = Object.keys(obj);
  if (!keys.length) return null;
  return firstString(obj[keys[0]]);
};

/**
 * ✅ FINAL POLICY (as you want):
 * 1) If code exists AND translation exists => show translated
 * 2) Else if code exists => show RAW code (KEY)   ✅✅✅
 * 3) Else show first field error message
 * 4) Else show backend message (if not "Validation error")
 * 5) Else show parsed message (not axios generic)
 * 6) Else show common.request_failed
 */
export const showApiErrorToast = (err, t) => {
  const tt = typeof t === "function" ? t : (key, fallback) => bridgeT(key, fallback);
  const fallback = tt("common.request_failed", "Request failed.");

  const parsed = parseApiError(err) || {};
  const extracted = extractBackendPayload(err);

  const backendCode =
    String(extracted?.code || "").trim() ||
    String(parsed?.code || "").trim() ||
    "";

  const translated = buildApiErrorKeyCandidates(backendCode)
    .map((key) => pickTranslationIfExists(tt, key))
    .find(Boolean);

  // ✅ 1) translated by code
  if (backendCode && translated) {
    toast(translated, TOAST_ERROR_OPTIONS);
    return { ...parsed, status: parsed?.status ?? extracted?.status, code: backendCode, message: translated, raw: parsed?.raw ?? err };
  }

  // ✅ 2) code exists but translation missing => show raw KEY
  if (backendCode && !translated) {
    toast(backendCode, TOAST_ERROR_OPTIONS);
    return { ...parsed, status: parsed?.status ?? extracted?.status, code: backendCode, message: backendCode, raw: parsed?.raw ?? err };
  }

  // 3) field errors
  const fields =
    extracted?.data?.extra?.fields ||
    parsed?.fieldErrors ||
    parsed?.extra?.fields ||
    null;

  const fieldMsg = pickFirstFieldError(fields);
  if (fieldMsg) {
    toast(fieldMsg, TOAST_ERROR_OPTIONS);
    return { ...parsed, status: parsed?.status ?? extracted?.status, code: parsed?.code, message: fieldMsg, raw: parsed?.raw ?? err };
  }

  // 4) backend message (if meaningful)
  const backendMsgRaw =
    String(extracted?.message || "").trim() ||
    (typeof extracted?.data === "string" ? extracted.data.trim() : "") ||
    "";

  const backendMsg = isUselessBackendMessage(backendMsgRaw) ? "" : backendMsgRaw;
  if (backendMsg && !isAxiosGenericMessage(backendMsg)) {
    toast(backendMsg, TOAST_ERROR_OPTIONS);
    return { ...parsed, status: parsed?.status ?? extracted?.status, code: parsed?.code, message: backendMsg, raw: parsed?.raw ?? err };
  }

  // 5) parsed message (not axios generic)
  const parsedMsgRaw = String(parsed?.message || "").trim();
  const parsedMsg = isAxiosGenericMessage(parsedMsgRaw) ? "" : parsedMsgRaw;

  const text = parsedMsg || fallback;

  toast(text, TOAST_ERROR_OPTIONS);

  return {
    ...parsed,
    status: parsed?.status ?? extracted?.status,
    code: parsed?.code,
    message: text,
    raw: parsed?.raw ?? err,
  };
};

// Re-export parser so consumers can import from "utils" if they want
export { parseApiError };
